// === WOONHUIS — payload construction, response merge, defaults, linking ===
// Pure functions extracted verbatim from WoonhuisScreen.tsx. Impure inputs are injected
// via opts (baseUrl/now/infofolioHerbwrd) so the full BFF payload chain is deterministic and
// golden-testable offline (see calc.test.ts). Mirrors inboedel/calc.ts.
import { buildGlassRequestBlock, hasGlassRebuildValue } from '../../../utils/glassPremium';
import { getCurrentLocale } from '../../../utils/currency';

export const CARRIER_CODE = 'R515';
export const PRODUCT_NAME = 'Woonhuis_2024';

// ======================================
// REQUEST/RESPONSE MAPPING
// ======================================

/**
 * Maps form data to calculation request payload
 *
 * INPUT FIELDS (sent to BFF):
 * - Contractdocument.PP.BA.* (Building address and details)
 * - Contractdocument.PP.OP.OP_GSS (Family composition)
 * - Contractdocument.PP.OP.OP_ERB (Excess)
 * - Contractdocument.PP.OP.OP_STORM (Storm coverage)
 * - Contractdocument.PP.OP.OP_CODE (Coverage type: 5004=Uitgebreid, 5006=Allrisk)
 * - Contractdocument.PP.VP.VP_GEBDAT (Birth date)
 * - Supportsection.Dekkingen.Hoofddekking (Uitgebreid/Allrisk)
 * - Supportsection.Dekkingen.Glas (Glass coverage Y/N)
 *
 * Conditionally includes GP (Glass) section based on Dekkingen selections.
 */
export const mapToCalculationRequest = (formData: any, _ppEkst: number, opts: { baseUrl: string; now: Date }) => {
  // Deep clone to avoid modifying original formData
  const requestData = JSON.parse(JSON.stringify(formData));

  // Ensure nested structure exists
  if (!requestData.Contractdocument) requestData.Contractdocument = {};
  if (!requestData.Contractdocument.PP) requestData.Contractdocument.PP = {};

  const pp = requestData.Contractdocument.PP;

  // AL (Algemeen) section - required for message function
  if (!requestData.Contractdocument.AL) {
    requestData.Contractdocument.AL = {};
  }
  if (!requestData.Contractdocument.AL.AL_APPLNM) {
    requestData.Contractdocument.AL.AL_APPLNM = 'SAA Verzekeringen';
  }
  if (!requestData.Contractdocument.AL.AL_URLNAAM) {
    requestData.Contractdocument.AL.AL_URLNAAM = opts.baseUrl;
  }
  if (!requestData.Contractdocument.AL.AL_FUNCTIE) {
    requestData.Contractdocument.AL.AL_FUNCTIE = '13';
  }
  delete requestData.Contractdocument.AL.AL_CPNAAM;

  // === Contractdocument: only AL and PP allowed (additionalProperties: false) ===
  // XG (status) comes back from BFF responses but is not allowed in requests
  for (const key of Object.keys(requestData.Contractdocument)) {
    if (key !== 'AL' && key !== 'PP') delete requestData.Contractdocument[key];
  }

  // === PP-level required fields ===
  if (!pp.PP_MYAAND) pp.PP_MYAAND = CARRIER_CODE;
  if (!pp.PP_PRODNM) pp.PP_PRODNM = PRODUCT_NAME;
  if (!pp.PP_PRODUCT) pp.PP_PRODUCT = pp.PP_PRODNM;
  if (!pp.PP_MYNAAM) pp.PP_MYNAAM = "Rhion";
  if (!pp.PP_HANDACC) pp.PP_HANDACC = 'N';
  pp.PP_STPRV = 'J';
  // PP_EKST - Servicevergoeding (config-driven via prolongationFee)
  pp.PP_EKST = 0;
  // PP_AFWAFSP not used for SAA - strip if present
  delete pp.PP_AFWAFSP;
  if (!pp.PP_INCWIJZ) pp.PP_INCWIJZ = 'I';
  if (pp.PP_IBAN === undefined) pp.PP_IBAN = '';
  if (!pp.PP_EXTERN) pp.PP_EXTERN = 'Opstal';
  pp.PP_TARDAT = Number(`${opts.now.getFullYear()}${String(opts.now.getMonth() + 1).padStart(2, '0')}${String(opts.now.getDate()).padStart(2, '0')}`);

  // === PP_HVVDAT (Hoofdvervaldatum) - first day of current month, next year ===
  if (!pp.PP_HVVDAT) {
    const hvvNow = opts.now;
    const hvvMonth = String(hvvNow.getMonth() + 1).padStart(2, '0');
    pp.PP_HVVDAT = Number(`${hvvNow.getFullYear() + 1}${hvvMonth}01`);
  }

  // === PP-level type enforcement ===
  if (pp.PP_CDUUMND !== undefined) pp.PP_CDUUMND = Number(pp.PP_CDUUMND);
  if (pp.PP_HVVDAT !== undefined) pp.PP_HVVDAT = Number(pp.PP_HVVDAT);
  if (pp.PP_INGDAT !== undefined) pp.PP_INGDAT = Number(pp.PP_INGDAT);
  if (pp.PP_TARDAT !== undefined) pp.PP_TARDAT = Number(pp.PP_TARDAT);
  if (pp.PP_BETTERM !== undefined) pp.PP_BETTERM = Number(pp.PP_BETTERM);
  // PP_PVADAT and PP_EXPDAT must be string per schema
  if (pp.PP_PVADAT !== undefined) pp.PP_PVADAT = String(pp.PP_PVADAT);
  if (pp.PP_EXPDAT !== undefined) pp.PP_EXPDAT = String(pp.PP_EXPDAT);

  // === Remove non-schema PP fields ===
  // WM is not in the PP schema (additionalProperties: false)
  delete pp.WM;

  // === BA (Building Address) section ===
  if (!pp.BA) pp.BA = {};
  delete pp.BA.BA_ZONNPAN;
  if (!pp.BA.BA_LEEGSTA) pp.BA.BA_LEEGSTA = 'N';
  if (!pp.BA.BA_PERMBEW) pp.BA.BA_PERMBEW = 'J';
  if (!pp.BA.BA_EIGENWO) pp.BA.BA_EIGENWO = 'J';
  if (!pp.BA.BA_BDRFBEL) pp.BA.BA_BDRFBEL = 'GW';
  if (!pp.BA.BA_BESTEMO) pp.BA.BA_BESTEMO = 'Woning';
  if (!pp.BA.BA_MONUMEN) pp.BA.BA_MONUMEN = 'N';
  if (!pp.BA.BA_LAND) pp.BA.BA_LAND = 'NL';
  if (pp.BA.BA_TOPPBGB === undefined) pp.BA.BA_TOPPBGB = 20;
  // BA_HUISNR must be integer
  if (pp.BA.BA_HUISNR !== undefined) pp.BA.BA_HUISNR = parseInt(String(pp.BA.BA_HUISNR), 10);

  // === BB (Bouwaard Bijzonderheden) section - required by schema ===
  if (!pp.BB) pp.BB = {};
  // Copy bouwaard fields from BA if available (BA and BB share these fields)
  if (!pp.BB.BB_AARDBW && pp.BA.BA_AARDBW) pp.BB.BB_AARDBW = pp.BA.BA_AARDBW;
  if (!pp.BB.BB_AARDBW) pp.BB.BB_AARDBW = 'E'; // Default: traditioneel (steen)
  if (!pp.BB.BB_AARDDAK && pp.BA.BA_AARDDAK) pp.BB.BB_AARDDAK = pp.BA.BA_AARDDAK;
  if (!pp.BB.BB_AARDDAK) pp.BB.BB_AARDDAK = 'H'; // Default: hard (pannen)

  // === OP (Opstal) section - schema requires array, not object ===
  let opObj = pp.OP || {};
  if (Array.isArray(opObj)) {
    opObj = opObj[0] || {};
  }
  // Always All Risk (5006)
  opObj.OP_CODE = '5006';
  opObj.OP_ENTREF = opObj.OP_CODE;
  if (opObj.OP_VOLGNUM === undefined) opObj.OP_VOLGNUM = 1;
  // OP_VEERB defaults to 0 to match the AFD/Rhion reference calc — a hardcoded 100 here lowered the
  // premium vs the reference (e.g. herbouwwaarde 288.400 gave OP_BTP 33,55 instead of 35,14).
  if (opObj.OP_VEERB === undefined) opObj.OP_VEERB = 0;
  if (opObj.OP_PPRC === undefined) opObj.OP_PPRC = 20;
  opObj.OP_PPRC = Number(opObj.OP_PPRC);
  if (!opObj.OP_GARONDV) opObj.OP_GARONDV = pp.BA?.BA_HERBVST === 'M' ? 'J' : 'N';
  // OP_MVZVKH should not be passed in the request
  delete opObj.OP_MVZVKH;
  if (!opObj.OP_GSS) opObj.OP_GSS = 'A';
  if (opObj.OP_ERB === undefined) opObj.OP_ERB = 0;
  opObj.OP_ERB = Number(opObj.OP_ERB);
  // Default OP_STORM to 'N' (storm deductible not waived)
  if (!opObj.OP_STORM) opObj.OP_STORM = 'N';
  // OP_ERBSTRM must be 0 when storm deductible is waived (J), 250 when not (N)
  if (opObj.OP_STORM === 'J') {
    opObj.OP_ERBSTRM = 0;
  } else if (opObj.OP_ERBSTRM === undefined || opObj.OP_ERBSTRM === null) {
    opObj.OP_ERBSTRM = 250;
  }
  opObj.OP_ERBSTRM = Number(opObj.OP_ERBSTRM);
  // OP_VERZSOM and BA_HERBWRD are the SAME number for woonhuis: the herbouwwaarde is the
  // insured sum. They have two sources though — Infofolio writes both, while the screen's
  // Herbouwwaarde input writes only OP_VERZSOM — so keep them in step in BOTH directions.
  // Without the OP -> BA leg, a manually entered rebuild value left BA_HERBWRD undefined:
  // the request went out missing a schema-REQUIRED field, and glass could not be priced at
  // all because the engine rates GP over BA_HERBWRD.
  if (!opObj.OP_VERZSOM && pp.BA.BA_HERBWRD) {
    opObj.OP_VERZSOM = Number(pp.BA.BA_HERBWRD);
  }
  if (!pp.BA.BA_HERBWRD && opObj.OP_VERZSOM) {
    pp.BA.BA_HERBWRD = Number(opObj.OP_VERZSOM);
  }
  if (pp.BA.BA_HERBWRD !== undefined) pp.BA.BA_HERBWRD = Number(pp.BA.BA_HERBWRD);
  // OP_WPREMBP — wijze premieberekening (default '4')
  if (!opObj.OP_WPREMBP) opObj.OP_WPREMBP = '4';
  // Keep OP as object (BFF expects object, not array)
  pp.OP = opObj;

  // === GP (Glass) — rated by the Midglas engine over BA_HERBWRD ===
  // Sent ONLY when a rebuild value is known: the engine declines the whole
  // request without one, which would leave the product unpriced, not just glass.
  if (hasGlassRebuildValue(pp.BA?.BA_HERBWRD)) {
    pp.GP = buildGlassRequestBlock();
  } else {
    delete pp.GP;
  }

  // VR (Verhuur) not offered for SAA - remove if present
  delete pp.VR;

  // === RI (Risico Indicatie) section ===
  if (!pp.RI) pp.RI = {};
  if (!pp.RI.RI_EGD) pp.RI.RI_EGD = 'N';
  if (!pp.RI.RI_OGB) pp.RI.RI_OGB = 'N';
  if (!pp.RI.RI_POLJUS) pp.RI.RI_POLJUS = 'N';
  if (!pp.RI.RI_OVRMED) pp.RI.RI_OVRMED = 'N';
  if (!pp.RI.RI_CGGDA) pp.RI.RI_CGGDA = 'N';
  if (!pp.RI.RI_STVOOP) pp.RI.RI_STVOOP = 'N';
  if (!pp.RI.RI_ALGVRWD) pp.RI.RI_ALGVRWD = 'J';

  // === VE (Verzekering Entiteit) section ===
  if (!pp.VE) pp.VE = {};
  if (!pp.VE.VE_VRWRKCD) pp.VE.VE_VRWRKCD = 'T';
  pp.VE.VE_MYAAND = 'K781';

  // === VP (Verzekeringspersoon) section - include address fields from BA ===
  const vpGebdat = pp.VP?.VP_GEBDAT;
  const vpParzakc = pp.VP?.VP_PARZAKC || 'P';
  const vpVrwrkcd = pp.VP?.VP_VRWRKCD;
  pp.VP = {
    VP_PARZAKC: vpParzakc,
    ...(vpGebdat !== undefined ? { VP_GEBDAT: Number(vpGebdat) } : { VP_GEBDAT: 19900101 }),
    ...(pp.BA?.BA_HUISNR !== undefined ? { VP_HUISNR: String(pp.BA.BA_HUISNR) } : {}),
    ...(pp.BA?.BA_PCODE ? { VP_PCODE: pp.BA.BA_PCODE } : {}),
    ...(pp.BA?.BA_PLAATS ? { VP_PLAATS: pp.BA.BA_PLAATS } : {}),
    ...(pp.BA?.BA_STRAAT ? { VP_STRAAT: pp.BA.BA_STRAAT } : {}),
    ...(vpVrwrkcd !== undefined ? { VP_VRWRKCD: vpVrwrkcd } : {}),
  };

  // === TP (Tariefpunt) section - required empty object ===
  if (!pp.TP) pp.TP = {};

  // === CL (Clausules) section - required array ===
  if (!pp.CL) pp.CL = [];

  // === VW (Voorwaarden) section - required array with minItems: 1 ===
  if (!pp.VW || pp.VW.length === 0) pp.VW = [{}];

  // === VL (Vrije Tekst) section - required empty object ===
  if (!pp.VL) pp.VL = {};

  // === Remove non-schema root fields (not needed for BFF calculation) ===
  // Supportsection (contains Dekkingen & Divers) is UI-only state, already read above
  delete requestData.Relatiedocument;
  delete requestData.Supportsection;
  delete requestData._completed;
  delete requestData._timestamp;

  // === Strip calculated fields that should not be sent back to BFF ===
  // These are output-only fields from previous calculations (BTP, TTSL, TKST, PKST, TKRT)
  const stripSuffixes = ['_TTSL'];
  const stripExact = ['PP_TKST', 'PP_PKST', 'PP_TKRT'];
  const stripFields = (obj: any) => {
    if (!obj || typeof obj !== 'object') return;
    for (const key of Object.keys(obj)) {
      if (stripExact.includes(key) || stripSuffixes.some(s => key.endsWith(s))) {
        delete obj[key];
      } else if (typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
        stripFields(obj[key]);
      }
    }
  };
  stripFields(pp);

  return {
    carrierCode: CARRIER_CODE,
    productName: PRODUCT_NAME,
    input: requestData
  };
};

/**
 * Maps calculation response to form data updates
 *
 * OUTPUT FIELDS (received from BFF):
 * - Contractdocument.PP.OP.OP_NTP (Base premium - Objectpremie)
 * - Contractdocument.PP.GP.GP_NTP (Glass premium - Glaspremie)
 * - Contractdocument.PP.OP.OP_POLIS (Policy number)
 * - Other calculated/enriched fields from BFF
 *
 * Merges response with current form data and clears premiums for unselected coverages.
 */
export const mapFromCalculationResponse = (response: any, currentFormData: any) => {
  if (!response || !response.output) {
    return currentFormData;
  }

  // Preserve user-input values before merge (BFF response may not include these)
  const userDekkingen = currentFormData?.Supportsection?.Dekkingen;
  const userWmInkjhkw = currentFormData?.Contractdocument?.PP?.WM?.WM_INKJHKW;
  const userOpVerzsom = currentFormData?.Contractdocument?.PP?.OP?.OP_VERZSOM;
  const userOpGss = (() => {
    const op = currentFormData?.Contractdocument?.PP?.OP;
    return Array.isArray(op) ? op[0]?.OP_GSS : op?.OP_GSS;
  })();
  const userBaZonnpan = currentFormData?.Contractdocument?.PP?.BA?.BA_ZONNPAN;
  const userBaHerbwrd = currentFormData?.Contractdocument?.PP?.BA?.BA_HERBWRD;
  const userBaToevoeg = currentFormData?.Contractdocument?.PP?.BA?.BA_TOEVOEG;
  const userVpAddress = {
    VP_PCODE: currentFormData?.Contractdocument?.PP?.VP?.VP_PCODE,
    VP_HUISNR: currentFormData?.Contractdocument?.PP?.VP?.VP_HUISNR,
    VP_TOEVOEG: currentFormData?.Contractdocument?.PP?.VP?.VP_TOEVOEG,
    VP_STRAAT: currentFormData?.Contractdocument?.PP?.VP?.VP_STRAAT,
    VP_PLAATS: currentFormData?.Contractdocument?.PP?.VP?.VP_PLAATS,
  };

  // Merge response with current form data
  const updatedData = {
    ...currentFormData,
    ...response.output
  };

  // Convert OP back from array to object (schema requires array, but UI uses object)
  if (updatedData?.Contractdocument?.PP?.OP && Array.isArray(updatedData.Contractdocument.PP.OP)) {
    updatedData.Contractdocument.PP.OP = updatedData.Contractdocument.PP.OP[0] || {};
  }

  // Restore VP address fields that BFF response does not include
  if (updatedData?.Contractdocument?.PP?.VP) {
    if (userVpAddress.VP_PCODE) updatedData.Contractdocument.PP.VP.VP_PCODE = userVpAddress.VP_PCODE;
    if (userVpAddress.VP_HUISNR) updatedData.Contractdocument.PP.VP.VP_HUISNR = userVpAddress.VP_HUISNR;
    if (userVpAddress.VP_TOEVOEG) updatedData.Contractdocument.PP.VP.VP_TOEVOEG = userVpAddress.VP_TOEVOEG;
    if (userVpAddress.VP_STRAAT) updatedData.Contractdocument.PP.VP.VP_STRAAT = userVpAddress.VP_STRAAT;
    if (userVpAddress.VP_PLAATS) updatedData.Contractdocument.PP.VP.VP_PLAATS = userVpAddress.VP_PLAATS;
  }

  // Restore user-input Woonhuisdekking values that BFF response may have wiped
  if (userWmInkjhkw != null) {
    if (!updatedData.Contractdocument.PP.WM) updatedData.Contractdocument.PP.WM = {};
    updatedData.Contractdocument.PP.WM.WM_INKJHKW = userWmInkjhkw;
  }
  if (userOpVerzsom != null) {
    if (!updatedData.Contractdocument.PP.OP) updatedData.Contractdocument.PP.OP = {};
    updatedData.Contractdocument.PP.OP.OP_VERZSOM = userOpVerzsom;
  }
  // OP_GSS (Gezinssamenstelling) - BFF response may not return it
  if (userOpGss && updatedData?.Contractdocument?.PP?.OP) {
    const opObj = Array.isArray(updatedData.Contractdocument.PP.OP)
      ? updatedData.Contractdocument.PP.OP[0]
      : updatedData.Contractdocument.PP.OP;
    if (opObj && !opObj.OP_GSS) {
      opObj.OP_GSS = userOpGss;
    }
  }
  // BA_ZONNPAN - stripped from calc request and not in BFF response; preserve user's choice
  if (userBaZonnpan != null) {
    if (!updatedData.Contractdocument.PP.BA) updatedData.Contractdocument.PP.BA = {};
    updatedData.Contractdocument.PP.BA.BA_ZONNPAN = userBaZonnpan;
  }
  if (userBaHerbwrd != null) {
    if (!updatedData.Contractdocument.PP.BA) updatedData.Contractdocument.PP.BA = {};
    updatedData.Contractdocument.PP.BA.BA_HERBWRD = userBaHerbwrd;
  }
  if (userBaToevoeg != null) {
    if (!updatedData.Contractdocument.PP.BA) updatedData.Contractdocument.PP.BA = {};
    updatedData.Contractdocument.PP.BA.BA_TOEVOEG = userBaToevoeg;
  }

  // Restore user's coverage selections (don't let response overwrite them)
  if (userDekkingen && updatedData.Supportsection) {
    updatedData.Supportsection.Dekkingen = {
      ...updatedData.Supportsection?.Dekkingen,
      // Preserve user's selections for coverages
      Hoofddekking: 'Allrisk',
      Glas: 'J',
    };
  } else if (updatedData.Supportsection) {
    // If no existing selections, set defaults
    if (!updatedData.Supportsection.Dekkingen) {
      updatedData.Supportsection.Dekkingen = {};
    }
    updatedData.Supportsection.Dekkingen.Hoofddekking = 'Allrisk';
    updatedData.Supportsection.Dekkingen.Glas = 'J';
  }

  // Recalculate PP_TTOT = base (OP) + glass (GP, rated by the Midglas engine)
  if (updatedData?.Contractdocument?.PP) {
    const opData = Array.isArray(updatedData.Contractdocument.PP.OP)
      ? updatedData.Contractdocument.PP.OP[0]
      : updatedData.Contractdocument.PP.OP;
    const basePremium = Number(opData?.OP_TTOT) || 0;
    const glassPremium = Number(updatedData.Contractdocument.PP.GP?.GP_TTOT) || 0;
    updatedData.Contractdocument.PP.PP_TTOT = basePremium + glassPremium;
  }

  // Remove VR if BFF response re-introduced it
  if (updatedData?.Contractdocument?.PP?.VR) {
    delete updatedData.Contractdocument.PP.VR;
  }

  return updatedData;
};

/**
 * Bake the configured hidden Woonhuis discount into section-level OP/GP premium fields and
 * recompute PP totals. Fields are floored to 2 decimals (per business rule: never round up).
 * Used both after the live BFF calculate response and at handleNext before the final save.
 */
export const applyWoonhuisDiscountInPlace = (
  data: any,
  discountCfg: { enabled: boolean; percentage: number; excludeCoverages: string[]; applyToData: boolean } | null | undefined,
) => {
  if (!discountCfg || !discountCfg.enabled || !(discountCfg.percentage > 0) || !discountCfg.applyToData) return;
  const factor = 1 - discountCfg.percentage / 100;
  const excluded = discountCfg.excludeCoverages || [];
  // Keep full precision — downstream math (× 1.21 tax → floor) needs the discounted BTP at
  // ~4 decimals to land on the correct cent. Mirrors AVP/Inboedel's bake: BFF inputs are
  // 2 decimals max, so 4 decimals strips JS float artefacts (20.00 × 0.925 →
  // 18.500000000000004 → clean 18.5). The wire-boundary strip in clientService floors the
  // outgoing PP / section premiums back to 2dp before hitting the BFF.
  const round4 = (v: number) => Math.round(Number(v) * factor * 10000) / 10000;
  const round2 = (v: number) => Math.round(v * 100) / 100;
  const pp = data?.Contractdocument?.PP;
  if (!pp) return;
  if (!excluded.includes('OP')) {
    const op = Array.isArray(pp.OP) ? pp.OP[0] : pp.OP;
    if (op) {
      if (op.OP_TTOT !== undefined) op.OP_TTOT = round4(op.OP_TTOT);
      if (op.OP_NTP !== undefined) op.OP_NTP = round4(op.OP_NTP);
      if (op.OP_TPP !== undefined) op.OP_TPP = round4(op.OP_TPP);
      if (op.OP_TASS !== undefined) op.OP_TASS = round4(op.OP_TASS);
      if (op.OP_BTP !== undefined) op.OP_BTP = round4(op.OP_BTP);
    }
  }
  if (!excluded.includes('GP') && pp.GP) {
    if (pp.GP.GP_TTOT !== undefined) pp.GP.GP_TTOT = round4(pp.GP.GP_TTOT);
    if (pp.GP.GP_NTP !== undefined) pp.GP.GP_NTP = round4(pp.GP.GP_NTP);
    if (pp.GP.GP_TPP !== undefined) pp.GP.GP_TPP = round4(pp.GP.GP_TPP);
    if (pp.GP.GP_TASS !== undefined) pp.GP.GP_TASS = round4(pp.GP.GP_TASS);
    if (pp.GP.GP_BTP !== undefined) pp.GP.GP_BTP = round4(pp.GP.GP_BTP);
  }
  const opData = Array.isArray(pp.OP) ? pp.OP[0] : pp.OP;
  pp.PP_NTP = round2((Number(opData?.OP_NTP) || 0) + (Number(pp.GP?.GP_NTP) || 0));
  pp.PP_TPP = round2((Number(opData?.OP_TPP) || 0) + (Number(pp.GP?.GP_TPP) || 0));
  pp.PP_TASS = round2((Number(opData?.OP_TASS) || 0) + (Number(pp.GP?.GP_TASS) || 0));
  pp.PP_BTP = round2((Number(opData?.OP_BTP) || 0) + (Number(pp.GP?.GP_BTP) || 0));
  pp.PP_TTOT = round2((Number(opData?.OP_TTOT) || 0) + (Number(pp.GP?.GP_TTOT) || 0));
  pp.PP_NTPVPK = pp.PP_NTP;
  // Marker so downstream helpers (computeWoonhuisDisplayPremium / NetPremium) know the BTPs
  // already carry the discount and don't re-apply it on top.
  pp._discountBaked = true;
};

/**
 * Cleans coverage sections before saving to workflow.
 * Base product (Woonhuis Allrisk - OP) is always present. Glass (GP) comes from the Midglas
 * engine on the calculation response (see utils/glassPremium.ts).
 */
export const cleanUnselectedCoveragePremiums = (data: any) => {
  const cleanedData = JSON.parse(JSON.stringify(data)); // Deep clone

  // Remove VR if present (not offered for SAA)
  delete cleanedData.Contractdocument?.PP?.VR;

  // Get base premium directly from BFF's OP_TTOT (Opstal total)
  const opSection = Array.isArray(data?.Contractdocument?.PP?.OP)
    ? data.Contractdocument.PP.OP[0]
    : data?.Contractdocument?.PP?.OP;
  const basePremium = Number(opSection?.OP_TTOT) || 0;
  const glassPremium = Number(data?.Contractdocument?.PP?.GP?.GP_TTOT) || 0;

  // Clean VW (conditions) array to remove Verhuur coverage terms
  if (cleanedData?.Contractdocument?.PP?.VW && Array.isArray(cleanedData.Contractdocument.PP.VW)) {
    cleanedData.Contractdocument.PP.VW = cleanedData.Contractdocument.PP.VW.filter((vw: any) => {
      const vrwOm = vw.VW_VRWOM || '';
      if (vrwOm.includes('Verhuur')) return false;
      return true;
    });
  }

  const newTotal = basePremium + glassPremium;

  // Normalize OP back to object (initializeWoonhuisDefaults wraps it in array for schema)
  // Summary screen and other consumers expect OP as object
  if (cleanedData?.Contractdocument?.PP?.OP && Array.isArray(cleanedData.Contractdocument.PP.OP)) {
    cleanedData.Contractdocument.PP.OP = cleanedData.Contractdocument.PP.OP[0] || {};
  }

  // Update PP_TTOT (product total) - this is what the summary page uses
  // Note: OP_TTOT stays as BFF's base premium, PP_TTOT reflects selected coverages
  if (cleanedData?.Contractdocument?.PP) {
    cleanedData.Contractdocument.PP.PP_TTOT = newTotal;
  }

  return cleanedData;
};

export { extractDefaults } from '../../../utils/extractDefaults';

// Behavior change (2026-07-03 review fix): the shared deepMerge has the Array.isArray guard the
// local copy lacked — arrays now survive the schema-defaults merge instead of corrupting to {"0": ...}.
export { deepMerge } from '../../../utils/deepMerge';

/**
 * Format currency for display - locale-aware (no decimals for whole amounts)
 * DIVERGES from inboedel/calc.ts formatCurrency (parseFloat + Math.round): this one strips all
 * non-digits before parsing, so "1234.56" formats differently. Which behaviour should win is an
 * open design decision — do not unify without deciding.
 */
export const formatCurrency = (value: number | string): string => {
  if (!value && value !== 0) return '';
  // Remove all non-digits to get raw number (works for both Dutch and English input)
  const num = Number(String(value).replace(/\D/g, ''));
  return num.toLocaleString(getCurrentLocale(), { maximumFractionDigits: 0 });
};

export { unformatCurrency } from '../../../utils/currency';

/**
 * Link relation data from client-information to Woonhuis Relatiedocument and Contractdocument
 * Converts client info fields to AFD format and syncs to both documents
 */
export const linkRelationData = (productData: any, relationData: any, opts: { now: Date }): any => {
  if (!relationData || !relationData.clientInfo) {
    return productData;
  }

  const result = { ...productData };

  // Ensure Relatiedocument.VP structure exists
  if (!result.Relatiedocument) {
    result.Relatiedocument = {};
  }
  if (!result.Relatiedocument.VP) {
    result.Relatiedocument.VP = {};
  }
  if (!result.Relatiedocument.BA) {
    result.Relatiedocument.BA = {};
  }

  // Ensure Contractdocument.PP.VP structure exists
  if (!result.Contractdocument) {
    result.Contractdocument = {};
  }
  if (!result.Contractdocument.PP) {
    result.Contractdocument.PP = {};
  }
  if (!result.Contractdocument.PP.VP) {
    result.Contractdocument.PP.VP = {};
  }

  // Ensure Contractdocument.PP.BA structure exists for address fields
  if (!result.Contractdocument.PP.BA) {
    result.Contractdocument.PP.BA = {};
  }

  // Link client information fields to VP (Verzekerde Persoon)
  if (relationData.clientInfo.initials) {
    result.Contractdocument.PP.VP.VP_VOORL = relationData.clientInfo.initials;
    result.Relatiedocument.VP.VP_VOORL = relationData.clientInfo.initials;
  }
  if (relationData.clientInfo.lastName) {
    result.Contractdocument.PP.VP.VP_ANAAM = relationData.clientInfo.lastName;
    result.Relatiedocument.VP.VP_ANAAM = relationData.clientInfo.lastName;
  }
  if (relationData.clientInfo.affix) {
    result.Contractdocument.PP.VP.VP_VOORV = relationData.clientInfo.affix;
    result.Relatiedocument.VP.VP_VOORV = relationData.clientInfo.affix;
  }
  if (relationData.clientInfo.gender) {
    result.Contractdocument.PP.VP.VP_GESLACH = relationData.clientInfo.gender;
    result.Relatiedocument.VP.VP_GESLACH = relationData.clientInfo.gender;
    // VP_AANHEFC / VP_TITULAT — zero-padded 5-char gender codes ('00001' / '00002').
    // Explicit clientInfo.salutationCode / clientInfo.titulature take precedence.
    const derived = relationData.clientInfo.gender === 'M' ? '00001'
      : relationData.clientInfo.gender === 'V' ? '00002' : undefined;
    if (derived && !relationData.clientInfo.salutationCode) result.Relatiedocument.VP.VP_AANHEFC = derived;
    if (derived && !relationData.clientInfo.titulature) result.Relatiedocument.VP.VP_TITULAT = derived;
  }
  if (relationData.clientInfo.salutationCode) {
    result.Relatiedocument.VP.VP_AANHEFC = relationData.clientInfo.salutationCode;
  }
  if (relationData.clientInfo.titulature) {
    result.Relatiedocument.VP.VP_TITULAT = relationData.clientInfo.titulature;
  }
  if (relationData.clientInfo.email) {
    result.Contractdocument.PP.VP.VP_EMAIL = relationData.clientInfo.email;
    result.Relatiedocument.VP.VP_EMAIL = relationData.clientInfo.email;
  }
  if (relationData.clientInfo.phone) {
    result.Contractdocument.PP.VP.VP_TELNUM = relationData.clientInfo.phone;
    result.Relatiedocument.VP.VP_TELNUM = relationData.clientInfo.phone;
  }

  // Link address fields from clientInfo to BA (Beschrijving Adres). Only seed empty BA
  // fields — clientInfo is the *original* address from the client-info screen and never
  // updates after that, so on remount it must not overwrite an address the user has since
  // edited on this product (the new value lives in step data / sharedAddress).
  // Note: Relatiedocument.VP should NOT have address fields when RisicoAdresGelijk = "J"
  if (relationData.clientInfo.street && !result.Contractdocument.PP.BA.BA_STRAAT) {
    result.Contractdocument.PP.BA.BA_STRAAT = relationData.clientInfo.street;
  }

  if (relationData.clientInfo.houseNumber && !result.Contractdocument.PP.BA.BA_HUISNR) {
    result.Contractdocument.PP.BA.BA_HUISNR = relationData.clientInfo.houseNumber;
  }

  if (relationData.clientInfo.houseNumberAddition && !result.Contractdocument.PP.BA.BA_TOEVOEG) {
    result.Contractdocument.PP.BA.BA_TOEVOEG = relationData.clientInfo.houseNumberAddition;
  }

  if (relationData.clientInfo.postalCode && !result.Contractdocument.PP.BA.BA_PCODE) {
    result.Contractdocument.PP.BA.BA_PCODE = relationData.clientInfo.postalCode;
  }

  if (relationData.clientInfo.city && !result.Contractdocument.PP.BA.BA_PLAATS) {
    result.Contractdocument.PP.BA.BA_PLAATS = relationData.clientInfo.city;
  }

  // Link dateOfBirth to VP_GEBDAT (convert from DD-MM-YYYY to YYYYMMDD number)
  if (relationData.clientInfo.dateOfBirth) {

    // Handle DD-MM-YYYY format from client information
    let dateStr = relationData.clientInfo.dateOfBirth.replace(/-/g, ''); // Remove dashes

    // Check if it's DD-MM-YYYY format (after removing dashes, length is 8)
    if (dateStr.length === 8) {
      // Convert DDMMYYYY to YYYYMMDD
      const day = dateStr.substring(0, 2);
      const month = dateStr.substring(2, 4);
      const year = dateStr.substring(4, 8);
      dateStr = year + month + day; // YYYYMMDD
    }

    const dateNum = Number(dateStr);
    const year = Math.floor(dateNum / 10000);

    // Validate the year is sensible (between 1900 and current year)
    const currentYear = opts.now.getFullYear();
    const isValidYear = year >= 1900 && year <= currentYear;

    if (!isNaN(dateNum) && dateStr.length === 8 && isValidYear) {
      result.Relatiedocument.VP.VP_GEBDAT = dateNum;
      result.Contractdocument.PP.VP.VP_GEBDAT = dateNum;
    } else {
      result.Relatiedocument.VP.VP_GEBDAT = 19900101;
      result.Contractdocument.PP.VP.VP_GEBDAT = 19900101;
    }
  } else {
    result.Relatiedocument.VP.VP_GEBDAT = 19900101;
    result.Contractdocument.PP.VP.VP_GEBDAT = 19900101;
  }

  // Clean up any stale address fields from Relatiedocument.VP
  // These should not be present when RisicoAdresGelijk = "J"
  const divers = result.Supportsection?.Divers;
  if (divers?.RisicoAdresGelijk === 'J') {
    delete result.Relatiedocument?.VP?.VP_PCODE;
    delete result.Relatiedocument?.VP?.VP_HUISNR;
    delete result.Relatiedocument?.VP?.VP_STRAAT;
    delete result.Relatiedocument?.VP?.VP_PLAATS;
    delete result.Relatiedocument?.VP?.VP_TOEVOEG;
  }

  return result;
};

/**
 * Link Inboedel data to Woonhuis (limited scope)
 * Woonhuis is FIRST product in the workflow, so address/building/GSS are entered here first.
 * This function only syncs non-address data that Inboedel may have (e.g., net annual income).
 */
export const linkInboedelAddress = (woonhuisData: any, inboedelData: any, _getStepDataFn: any, opts?: { infofolioHerbwrd?: string | null }): any => {
  if (!inboedelData) {
    return woonhuisData;
  }

  const result = JSON.parse(JSON.stringify(woonhuisData));
  const inboedelBA = inboedelData?.Contractdocument?.PP?.BA;

  // Sync Net Annual Income from Inboedel (WM_INKJHKW) - for back-navigation case
  const inboedelWM = inboedelData?.Contractdocument?.PP?.WM;
  if (inboedelWM && inboedelWM.WM_INKJHKW) {
    if (!result.Contractdocument.PP.WM) {
      result.Contractdocument.PP.WM = {};
    }
    result.Contractdocument.PP.WM.WM_INKJHKW = inboedelWM.WM_INKJHKW;
  }

  // Sync Herbouwwaarde (rebuild value) from Inboedel to both BA_HERBWRD and OP_VERZSOM
  // BA_HERBWRD may be stripped from Inboedel data by calculation cleanup, so also check sessionStorage
  const herbwrdFromInboedel = inboedelBA?.BA_HERBWRD || Number(opts?.infofolioHerbwrd ?? sessionStorage.getItem('infofolio_BA_HERBWRD')) || 0;
  if (herbwrdFromInboedel > 0) {
    if (!result.Contractdocument.PP.OP) {
      result.Contractdocument.PP.OP = {};
    }
    if (!result.Contractdocument.PP.BA) {
      result.Contractdocument.PP.BA = {};
    }
    result.Contractdocument.PP.BA.BA_HERBWRD = herbwrdFromInboedel;
    result.Contractdocument.PP.OP.OP_VERZSOM = herbwrdFromInboedel;
  }

  // Sync Vaststelling herbouwwaarde from Inboedel
  if (inboedelBA && inboedelBA.BA_HERBVST) {
    if (!result.Contractdocument.PP.BA) {
      result.Contractdocument.PP.BA = {};
    }
    result.Contractdocument.PP.BA.BA_HERBVST = inboedelBA.BA_HERBVST;
  }

  return result;
};
